import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { buildDisplayName, formatDisplayName } from '../../utils/nameFormatting';
import '../../styles/components/dashboard/RecentConversations.css';

const CONVERSATIONS_API = 'https://3mv40ugihi.execute-api.eu-west-2.amazonaws.com/dev/conversations';
const PROFILE_API = 'https://0zr99zyhbk.execute-api.eu-west-2.amazonaws.com/dev';
const RECENT_LIMIT = 5;
const timestampValue = (value) => Date.parse(value) || 0;

const RecentConversations = () => {
  const { authFetch } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const load = async () => {
      setLoading(true);
      setError(false);

      try {
        const response = await authFetch(CONVERSATIONS_API, { signal });
        if (!response.ok) throw new Error('Conversation request failed');
        const payload = await response.json();
        if (!Array.isArray(payload.conversations)) throw new Error('Invalid conversations response');

        const recent = payload.conversations
          .filter((conversation) => conversation.otherUserSub)
          .sort((left, right) => timestampValue(right.lastTimestamp) - timestampValue(left.lastTimestamp))
          .slice(0, RECENT_LIMIT);

        const enriched = await Promise.all(recent.map(async (conversation) => {
          let name = formatDisplayName(conversation.otherParticipantName || '');
          if (!name) {
            try {
              const profileResponse = await fetch(
                `${PROFILE_API}/users/${encodeURIComponent(conversation.otherUserSub)}/profile`,
                { signal }
              );
              if (profileResponse.ok) {
                const profile = await profileResponse.json();
                name = buildDisplayName(profile.first_name, profile.second_name);
              }
            } catch {
              // A missing profile should not hide an otherwise usable conversation.
            }
          }
          return { ...conversation, displayName: name || 'User' };
        }));

        if (!signal.aborted) setConversations(enriched);
      } catch {
        if (!signal.aborted) setError(true);
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, [authFetch, attempt]);

  return (
    <section className="recent-conversations" aria-labelledby="recent-conversations-title">
      <h2 id="recent-conversations-title">Recent conversations</h2>

      {loading && <p className="recent-conversations__state" role="status">Loading conversations…</p>}

      {!loading && error && (
        <div className="recent-conversations__state" role="alert">
          <p>We could not load your conversations.</p>
          <button type="button" onClick={() => setAttempt((value) => value + 1)}>Try again</button>
        </div>
      )}

      {!loading && !error && conversations.length === 0 && (
        <p className="recent-conversations__state">No conversations yet.</p>
      )}

      {!loading && !error && conversations.length > 0 && (
        <ul className="recent-conversations__list">
          {conversations.map((conversation) => {
            const date = new Date(conversation.lastTimestamp);
            const hasDate = Boolean(conversation.lastTimestamp) && !Number.isNaN(date.getTime());

            return (
              <li key={conversation.chatId || conversation.otherUserSub}>
                <Link
                  className="recent-conversations__row"
                  to={`/messages?conversation=${encodeURIComponent(conversation.otherUserSub)}`}
                >
                  <span className="recent-conversations__avatar" aria-hidden="true">
                    {conversation.displayName.charAt(0).toUpperCase()}
                  </span>
                  <span className="recent-conversations__body">
                    <span className="recent-conversations__topline">
                      <strong>{conversation.displayName}</strong>
                      {hasDate && (
                        <time dateTime={date.toISOString()} title={date.toLocaleString('en-GB')}>
                          {date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </time>
                      )}
                    </span>
                    <span className="recent-conversations__preview">{conversation.lastMessage || 'No messages yet'}</span>
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      <Link className="recent-conversations__view-all" to="/messages">View all conversations</Link>
    </section>
  );
};

export default RecentConversations;
